import React, { forwardRef, useMemo } from 'react';

import type { ChipProps } from '@mui/material';
import { Chip, Typography, useTheme } from '@mui/material';
import type { AccountOption } from 'src/components';
import { AlineAvatar } from 'src/components';
import { XIcon } from 'src/components/icons/XIcon';
import { getIsUserSubset } from 'src/helpers';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<StyleProps>()((_theme, { chipColor, size }) => ({
  chip: {
    border: `1px solid ${chipColor}`,
    height: !size || size === 'medium' ? 26 : 20,
  },
  icon: {
    '&:hover': {
      color: chipColor,
    },
    color: chipColor,
    height: !size || size === 'medium' ? 14 : 12,
    width: !size || size === 'medium' ? 14 : 12,
  },
  text: {
    color: chipColor,
  },
}));

interface StyleProps {
  chipColor: string;
  size: ChipProps['size'];
}

interface AccountChipProps extends ChipProps {
  accountInfo: AccountOption;
}

export const AccountChip = forwardRef(function AccountChip(
  props: AccountChipProps,
  ref: React.Ref<HTMLDivElement>
) {
  const { accountInfo, size = 'medium', ...rest } = props;
  const theme = useTheme();

  const { avatarInfo, chipColor, label } = useMemo((): {
    avatarInfo: UserSubset | string;
    chipColor: string;
    label: string;
  } => {
    const guestColor = theme.appColors.accent1;

    if (accountInfo.__typename === 'NewUser') {
      return {
        avatarInfo: accountInfo.email.charAt(0),
        chipColor: guestColor,
        label: accountInfo.email,
      };
    }

    if (getIsUserSubset(accountInfo)) {
      return {
        avatarInfo: accountInfo,
        chipColor: accountInfo.avatarColor ?? guestColor,
        label: accountInfo.email,
      };
    }

    return {
      avatarInfo: accountInfo.name.charAt(0),
      chipColor: accountInfo.avatarColor ?? guestColor,
      label: accountInfo.name,
    };
  }, [accountInfo, theme.appColors]);

  const { classes } = useStyles({ chipColor, size });

  return (
    <Chip
      ref={ref}
      avatar={
        <AlineAvatar
          avatarInfo={avatarInfo}
          // Note the styles key on AlineAvatar does not work here because MUI injects some styles with a higher specificity in Chip.  So, we use inline style on purpose here.
          style={{ backgroundColor: chipColor, color: theme.palette.common.white, marginLeft: 0 }}
        />
      }
      classes={{ root: classes.chip }}
      deleteIcon={<XIcon className={classes.icon} viewBox="0 0 12 12" />}
      label={
        <Typography noWrap align="left" className={classes.text}>
          {label}
        </Typography>
      }
      size={size}
      variant="outlined"
      {...rest}
    />
  );
});
